/**
 * @file `ReaderErrorBoundary` — catches render faults anywhere in the
 *       chapter reader subtree and shows a recoverable fallback instead
 *       of white-screening the whole SPA.
 * @module @labcraft/lab/ui/components/reader-error-boundary
 *
 * The reader composes a lot of moving parts — the objective runtime
 * (`course-kit`), dynamically-imported MDX chapter modules, the split
 * layout, the terminal panes — and a thrown render exception in any of
 * them unwinds to the root and blanks `#root` (an empty page, no
 * feedback). One concrete case prompted this: a chapter whose `spec`
 * lacked its `objectives` list (stale/malformed content) crashed the
 * objective progress bar on mount. The page-level `OkChapterView` now
 * guards that specific shape, but a render boundary is the general
 * backstop — any unforeseen reader fault degrades to "reload" rather
 * than a blank screen. Boundaries must be class components (no hook
 * equivalent for `componentDidCatch`).
 */

import { Component, type ErrorInfo, type ReactNode } from 'react'
import styles from './reader_error_boundary.module.css'

/**
 * Props for {@link ReaderErrorBoundary}.
 *
 * @property children - The reader subtree to guard.
 */
type ReaderErrorBoundaryProps = {
  children: ReactNode
}

/**
 * State for {@link ReaderErrorBoundary}.
 *
 * @property error - The caught render error, or `null` while healthy.
 */
type ReaderErrorBoundaryState = {
  error: Error | null
}

/**
 * Error boundary around the chapter reader. Renders its children
 * normally; on a caught render error it swaps to a calm full-pane
 * "reload" fallback. See the file header for the failure it prevents.
 */
export class ReaderErrorBoundary extends Component<
  ReaderErrorBoundaryProps,
  ReaderErrorBoundaryState
> {
  override state: ReaderErrorBoundaryState = { error: null }

  /**
   * React error-boundary hook — derive the fallback-rendering state
   * from a thrown child error.
   *
   * @param error - The error thrown during a child render.
   * @returns The next state carrying the error.
   */
  static getDerivedStateFromError(error: Error): ReaderErrorBoundaryState {
    return { error }
  }

  /**
   * Log the caught fault with its component stack — white-screening on
   * a reader crash is the failure this boundary exists to prevent, so
   * leave enough breadcrumbs to debug the underlying throw.
   *
   * @param error - The caught error.
   * @param info - React-supplied component stack.
   */
  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[reader] render crash', error, info.componentStack)
  }

  /** Full reload — the honest recovery for a content/render fault. */
  private readonly onReload = (): void => {
    window.location.reload()
  }

  /**
   * Render children while healthy; the reload fallback once a child
   * render has thrown.
   *
   * @returns The guarded subtree or the fallback element.
   */
  override render(): ReactNode {
    if (this.state.error === null) return this.props.children
    return (
      <div className={styles.fallback} role="alertdialog" aria-modal>
        <div className={styles.card}>
          <span className={styles.eyebrow}>This chapter hit a snag</span>
          <h2 className={styles.title}>The reader couldn’t render.</h2>
          <p className={styles.body}>
            Something in this chapter’s content failed to load. Reloading
            usually clears it — your lab and progress are safe.
          </p>
          <button
            className={styles.btn}
            type="button"
            onClick={this.onReload}
            autoFocus>
            Reload
          </button>
        </div>
      </div>
    )
  }
}
